import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { Button, IconButton } from "../../../ui";
import Input from "../../input/input";
import InputDescricao from "../../input-descricao/inputDescricao";
import { InputSwitch } from "../../input-switch/switch";
import InputMascarado from "../../input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../../../utils/mascaras/mascaraMoedaReal";
import { SelectUnitOfMeasure } from "../../select/SelectUnitOfMeasure";

interface CargosFormPartProps {
  methods: UseFormReturn<any>;
  disabled?: boolean;
  // Adicione o tema se necessário
}

export function CargosFormPart({
  methods,
  disabled = false,
}: CargosFormPartProps) {
  const {
    control,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "positionsDto",
  });

  const theme = useTheme();
  const tb = useMediaQuery(theme.breakpoints.down(720));
  const mb = useMediaQuery(theme.breakpoints.down(460));

  return (
    <Box>
      <Typography variant="h5" padding="20px 0">
        Cargos da Obra
      </Typography>
      {fields.map((position, idx) => (
        <Box
          key={position.id || idx}
          mb={2}
          p={2}
          border="1px solid #eee"
          borderRadius={2}
        >
          <Box
            display={tb ? "block" : "grid"}
            gridTemplateColumns={tb ? "1fr" : "10fr 1fr"}
            gap={2}
            alignItems="center"
          >
            <Box
              display={mb ? "block" : "flex"}
              gap={2}
              alignItems="center"
              margin={tb ? "0 0 10px 0" : "0"}
            >
              <Input
                name={`positionsDto.${idx}.name`}
                label="Nome do cargo"
                methods={methods}
                disabled={disabled}
              />
            </Box>
            <Box
              display={mb ? "block" : "flex"}
              gap={2}
              alignItems="center"
              width={tb ? "100%" : "20%"}
              maxWidth={tb ? "100%" : "20%"}
            >
              <InputSwitch
                name={`positionsDto.${idx}.active`}
                label="Ativo"
                methods={methods}
                disabled={disabled}
              />
              <IconButton
                title="Deletar cargo"
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                onClick={() => {
                  if (window.confirm("Tem certeza que deseja deletar este cargo?")) {
                    remove(idx);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </Box>
          </Box>
          <Box mt={2}>
            <InputDescricao
              name={`positionsDto.${idx}.description`}
              label="Descrição"
              methods={methods}
              disabled={disabled}
              minHeight={80}
            />
          </Box>
          <PrecosFormPart
            methods={methods}
            positionIdx={idx}
            disabled={disabled}
            mdDown={tb}
          />
        </Box>
      ))}
      <Button
        type="button"
        onClick={() =>
          append({
            name: "",
            description: "",
            active: true,
            priceDto: [],
          })
        }
        className="mt-4"
        disabled={disabled}
      >
        Adicionar cargo
      </Button>
    </Box>
  );
}

// Subcomponente para preços dentro do cargo
function PrecosFormPart({
  methods,
  positionIdx,
  disabled = false,
  mdDown = false,
}: {
  methods: UseFormReturn<any>;
  positionIdx: number;
  disabled?: boolean;
  mdDown?: boolean;
}) {
  const {
    control,
    formState: { errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: `positionsDto.${positionIdx}.priceDto`,
  });

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" mb={2}>
        Preços do cargo
      </Typography>
      {fields.map((price, priceIdx) => (
        <Box
          key={price.id || priceIdx}
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "2fr 1.5fr auto 44px"}
          gap={2}
          alignItems="start"
          mb={2}
        >
          <InputMascarado
            name={`positionsDto.${positionIdx}.priceDto.${priceIdx}.value`}
            methods={methods}
            label="Valor do preço por modalidade"
            mask={mascaraMoedaReal}
          />
          <SelectUnitOfMeasure label="Modelo de Trabalho" name={`positionsDto.${positionIdx}.priceDto.${priceIdx}.modality`} control={control} />
          <InputSwitch
            name={`positionsDto.${positionIdx}.priceDto.${priceIdx}.active`}
            label="Ativo"
            methods={methods}
            disabled={disabled}
          />
          <IconButton
            title="Remover preço"
            className="mt-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            onClick={() => remove(priceIdx)}
          >
            <Trash2 className="h-4 w-4" />
          </IconButton>
        </Box>
      ))}
      <Button
        type="button"
        onClick={() =>
          append({
            value: 0,
            modality: "",
            active: true,
          })
        }
        size="sm"
        className="mt-1"
        disabled={disabled}
      >
        Adicionar preço
      </Button>
    </Box>
  );
}
